import {IWedEdBar, IWedEditorMain, OWedManagerConfig, WedMgr} from "back/edit/wed/wedEditor";
import {IReg, REG} from "lib/commons/registry";
import {DOM} from "lib/commons/xml/dom";
import {Action, IActionToggle} from "lib/commons/actions";
import {OWedEditorBoxConfig, WedEditorBox} from "back/edit/wed/wedEditorBox";
import {IResolverPointer} from "lib/commons/io/io";
import {DOMSH} from "lib/commons/xml/domsh";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {SkRuleAttr, SkRuleElt} from "lib/edit/schema/schemaPatterns";
import {HelpViews, OHelpViewsInit} from "back/help/views/helpViews";
import {BASIS} from "back/commons/basis";
import {IView, IViewContainer, OViewVisitOptions} from "lib/commons/views";
import {IJmlObj} from "lib/commons/xml/jml";
import {IDocHolderSync} from "lib/edit/docHolder";
import {IS_EltWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {helpId} from "back/help/helpApi";

/**
 * Initialisation dynamique de la feature d'aide
 *
 * @param rootHelpId Racine d'aide associé à l'éditeur de ce contenu
 * @param helpDb Db d'aide à charger et interroger.
 * @param reg Reg qui va être associé à editorConfig. ATTENTION ce reg va être surchargé, il DOIT DEJA être dédié à cet éditeur.
 * @param editorConfig
 * @param wedMgrConfig
 * @param editor editor qui va être initialisé avec editorConfig et wedMgrConfig
 */
export function initHelpOnEditor(helpInit: OHelpViewsInit, reg: IReg<IResolverPointer>, editorConfig: OWedEditorBoxConfig, wedMgrConfig: OWedManagerConfig, editor: WedEditorBox) {
	reg.addToList("actions:wed:commonbar:end", "help", 1, new ToggleHelpAction(helpInit), 101);
	reg.addToList("accelKeys:wed:global", "F1", 1, new ShowHelpFromSelAction(helpInit));
}

class WedHelpBar extends HTMLElement implements CustomElement, IWedEdBar, IViewContainer {

	wedEditor: IWedEditorMain;
	view: HelpViews;

	constructor() {
		super();
		this.id = HELPBAR_ID;
	}

	async initHelpBar(wedEditor: IWedEditorMain, helpInit: OHelpViewsInit): Promise<this> {
		this.wedEditor = wedEditor;
		this.setAttribute("label", "Aide"); //api View
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		wedEditor.reg.installSkin(this.localName, sr);
		this.view = sr.appendChild(new HelpViews().initialize(BASIS.newInit(helpInit, wedEditor.reg)));
		wedEditor.barLayout.showBar(this);
		await this.view.initializedAsync;
		return this;
	}

	showBar(show: boolean) {
		if (show) {
			if (DOM.setHidden(this, false)) this.wedEditor.barLayout.showBar(this);
		} else if (DOM.setHidden(this, true)) {
			this.wedEditor.barLayout.onHideBar(this);
		}
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (options?.visible && this.hidden) return;
		return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (options?.visible && this.hidden) return;
		return visitor(this.view);
	}
}

REG.reg.registerSkin("wed-helpbar", 1, /*language=CSS*/ `
	:host {
		flex: 1 1 30%;
		display: flex;
		min-height: 100px;
		min-width: 100px;
		flex-direction: column;
	}
`);

customElements.define('wed-helpbar', WedHelpBar);

export class ToggleHelpAction extends Action<IWedEditorMain> implements IActionToggle<IWedEditorMain> {

	constructor(public helpInit: OHelpViewsInit) {
		super("help");
		this._label = "Aide";
		this._icon = "/@skin@/edit/wed/help/help.svg";
	}

	isToggle(): this is IActionToggle<IWedEditorMain> {return true}

	getDatas(api: 'toggle', ctx: IWedEditorMain): boolean | null {
		const bar = ctx.barLayout.getBar(HELPBAR_ID);
		return bar && !bar.hidden;
	}

	execute(ctx: IWedEditorMain, ev?: Event): any | 'noPreventDefault' {
		const bar: WedHelpBar = ctx.barLayout.getBar(HELPBAR_ID) as WedHelpBar;
		if (bar) {
			bar.showBar(bar.hidden);
		} else getOrCreateHelpBar(ctx, this.helpInit);
	}
}

/** Appelé sur accelKey F1 */
export class ShowHelpFromSelAction extends Action<IWedEditorMain> implements IActionToggle<IWedEditorMain> {

	constructor(public helpInit: OHelpViewsInit) {
		super("help");
	}

	async execute(ctx: IWedEditorMain, ev?: Event) {
		return (await getOrCreateHelpBar(ctx, this.helpInit)).view.showLiveHelpId(findHelpFromLastFocus(ctx.wedMgr), true);
	}
}

async function getOrCreateHelpBar(editor: IWedEditorMain, helpInit: OHelpViewsInit): Promise<WedHelpBar> {
	let bar = editor.barLayout.getBar(HELPBAR_ID) as WedHelpBar;
	if (!bar) {
		bar = await new WedHelpBar().initHelpBar(editor, helpInit);
		//Activation dynamique par rapport à le sel
		editor.wedMgr.listeners.on("getFocus", (wedMgr: WedMgr) => {
			bar.view.showLiveHelpFromSel(() => findHelpFromLastFocus(wedMgr))
		});
	} else bar.showBar(true);
	return bar;
}

/** Approche 1 : dépend la sel courante : permet l'aide contextuelle dans la textPrim. */
function findHelpFromSel(wedMgr: WedMgr): helpId | undefined {
	const sel = wedMgr.getCurrentSel();
	if (sel) {
		let xa = sel.startInText ? XA.up(sel.start) : sel.start;
		return findHelpFromXa(wedMgr.docHolder, xa, sel.virtualPath);
	}
}

/** Approche 2 : dépend du lastFocus : plus stable, exclus l'aide contextuelle dans la textPrim. */
function findHelpFromLastFocus(wedMgr: WedMgr): helpId | undefined {
	const elt = wedMgr.lastFocus;
	if (elt?.isConnected) {
		const wedlet = DOMSH.findFlatParentEltOrSelf(elt, wedMgr.wedEditor.rootNode, IS_EltWedlet)?.wedlet;
		if (wedlet) return findHelpFromXa(wedMgr.docHolder, wedlet.wedAnchor, WEDLET.buildVirtualPath(wedlet));
	}
}

function findHelpFromXa(docHolder: IDocHolderSync, xa: IXAddr, virtualPath: Array<IJmlObj | '#' | '@'> | null): helpId {
	let helpId = (docHolder.getStruct(xa, virtualPath) as SkRuleElt | SkRuleAttr)?.helpId;
	while (!helpId) {
		if (xa.length > 1) xa = XA.up(xa);
		else break;
		if (virtualPath?.length > 0) virtualPath.length--;
		helpId = (docHolder.getStruct(xa, virtualPath) as SkRuleElt | SkRuleAttr)?.helpId;
	}
	return helpId;
}

const HELPBAR_ID = "helpBar";